/**
 * `/privacy`: one row per k threshold, its minimum group size and whether
 * the gate enforces it. A switched-off threshold withholds nothing.
 */

import { useState, type ReactNode } from 'react';

import { Page, PageHeader } from '../../chrome/page';
import { failureSentence, LoadingState, QueryStates } from '../../chrome/state';
import { ApiFailureError, type ApiFailure } from '../../lib/http/errors';
import { Button } from '../../ui/button';
import { Card, CardContent } from '../../ui/card';
import { Input } from '../../ui/input';
import { Switch } from '../../ui/switch';
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../ui/table';
import { usePrivacyThresholds, useSavePrivacyThreshold } from '../api/queries';
import type { PrivacyThreshold } from '../api/schemas';
import { opsTitles } from '../shell/titles';
import { t } from './messages';

function ThresholdRow(props: { threshold: PrivacyThreshold }): ReactNode {
  const { threshold } = props;
  const save = useSavePrivacyThreshold();
  const [value, setValue] = useState(String(threshold.value));
  const [enabled, setEnabled] = useState(threshold.enabled);
  const [failure, setFailure] = useState<ApiFailure | null>(null);

  const name = t(`${threshold.key}.name`);
  const parsed = Number(value);
  const valid = Number.isInteger(parsed) && parsed >= 1;
  const dirty = value !== String(threshold.value) || enabled !== threshold.enabled;

  function handleSave(): void {
    setFailure(null);
    save.mutate(
      { key: threshold.key, value: parsed, enabled },
      {
        onError: (error) =>
          setFailure(
            error instanceof ApiFailureError ? error.failure : { kind: 'server', status: 0 },
          ),
      },
    );
  }

  return (
    <>
      <TableRow data-key={threshold.key}>
        <TableCell className="min-w-48 whitespace-normal">
          <div className="flex flex-col gap-0.5">
            <span className="font-medium">{name}</span>
            <span className="text-caption text-muted-foreground">{t(`${threshold.key}.hint`)}</span>
          </div>
        </TableCell>
        <TableCell>
          <Input
            type="number"
            min={1}
            step={1}
            aria-label={t('aria.value', { name })}
            aria-invalid={!valid}
            value={value}
            onChange={(event) => setValue(event.target.value)}
            className="w-20"
          />
        </TableCell>
        <TableCell>
          <Switch
            aria-label={t('aria.enabled', { name })}
            checked={enabled}
            onCheckedChange={setEnabled}
          />
        </TableCell>
        <TableCell className="text-end">
          {dirty && (
            <Button
              type="button"
              size="sm"
              disabled={!valid || save.isPending}
              aria-label={t('aria.save', { name })}
              onClick={handleSave}
            >
              {t('save')}
            </Button>
          )}
        </TableCell>
      </TableRow>
      {(failure !== null || !valid) && (
        <TableRow>
          <TableCell colSpan={4}>
            <p role="alert" className="text-caption text-destructive">
              {failure === null || failure.kind === 'invalid'
                ? t('invalid')
                : failureSentence(failure)}
            </p>
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

export function Privacy(): ReactNode {
  const query = usePrivacyThresholds();
  return (
    <Page>
      <PageHeader title={opsTitles.t('privacy')} />
      <QueryStates<PrivacyThreshold[]> query={query} loading={<LoadingState label={t('title')} />}>
        {(thresholds) => (
          <Card>
            <CardContent className="px-0">
              <Table label={t('title')}>
                <TableCaption className="sr-only">{t('title')}</TableCaption>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t('header.threshold')}</TableHead>
                    <TableHead>{t('header.value')}</TableHead>
                    <TableHead>{t('header.enabled')}</TableHead>
                    <TableHead>
                      <span className="sr-only">{t('save')}</span>
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {thresholds.map((threshold) => (
                    <ThresholdRow
                      key={`${threshold.key}-${threshold.value}-${threshold.enabled}`}
                      threshold={threshold}
                    />
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}
      </QueryStates>
    </Page>
  );
}
